import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useFactory } from '../../../../context/FactoryContext';

export const ExtCompanyModal = ({ isOpen, onClose, onSave, company = null }) => {
  const isEdit = !!company;
  const { factories = [], loadingFactories, selectedFactory } = useFactory();

  const [formData, setFormData] = useState({
    tax_id: '',
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    active: true,
    notes: '',
    factories: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const getFactoryIds = (rawFactories) => {
    if (!Array.isArray(rawFactories)) return [];
    return rawFactories
      .map((f) => (typeof f === 'object' && f !== null ? (f.id || f.ID) : f))
      .filter((id) => id !== undefined && id !== null);
  };

  {/*Get factory name*/ }
  const getFactoryInitials = (factory) => {
    const code = factory.asset_code || factory.assetCode || factory.code || factory.Code;
    if (code) return String(code).trim().toUpperCase();
  };

  const handleToggleFactory = (factoryId) => {
    setFormData((prev) => {
      const current = prev.factories || [];
      const exists = current.includes(factoryId);
      const nextFactories = exists
        ? current.filter((id) => id !== factoryId)
        : [...current, factoryId];
      setFormErrors((errs) => ({
        ...errs,
        factories: nextFactories.length === 0 ? 'At least one factory must be selected' : undefined,
      }));
      return {
        ...prev,
        factories: nextFactories,
      };
    });
  };

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setFormErrors({});
      if (company) {
        setFormData({
          tax_id: company.tax_id || '',
          name: company.name || '',
          contact_name: company.contact_name || '',
          email: company.email || '',
          phone: company.phone || '',
          address: company.address || '',
          website: company.website || '',
          active: company.active !== undefined ? company.active : true,
          notes: company.notes || '',
          factories: getFactoryIds(company.factories),
        });
      } else {
        const defaultFactory = factories.find(
          (f) =>
            (f.asset_code || f.assetCode || f.code || f.Code || f.name || '').toUpperCase() ===
            selectedFactory?.toUpperCase()
        );
        const defaultFactoryId = defaultFactory?.id || defaultFactory?.ID;
        setFormData({
          tax_id: '',
          name: '',
          contact_name: '',
          email: '',
          phone: '',
          address: '',
          website: '',
          active: true,
          notes: '',
          factories: defaultFactoryId ? [defaultFactoryId] : [],
        });
      }
    }
  }, [isOpen, company, selectedFactory, factories]);


  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Company Name is required';
    }
    if (!formData.tax_id.trim()) {
      errors.tax_id = 'Tax ID is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/
      .test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    if (!formData.factories || formData.factories.length === 0) {
      errors.factories = 'At least one factory must be selected';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const FIELD_LIMITS = {
    name: 255,
    tax_id: 20,
    contact_name: 255,
    email: 255,
    phone: 20,
    website: 255,
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    const limit = FIELD_LIMITS[name];
    if (limit !== undefined && value.length > limit) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.message || 'An error occurred while saving.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-2xl border border-gray-700 bg-gray-800 shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-700 px-6 py-4">
          <h2 className="text-xl font-semibold text-white">
            {isEdit ? 'Edit External Company' : 'Add External Company'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg text-red-200 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Company Name<span className='text-red-400'> *</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={255}
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.name ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                  placeholder="e.g. Acme Corporation"
                />
                {formErrors.name && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tax ID / NIF<span className='text-red-400'> *</span>
                </label>
                <input
                  type="text"
                  name="tax_id"
                  value={formData.tax_id}
                  onChange={handleChange}
                  maxLength={20}
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.tax_id ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                  placeholder="e.g. B0000000C"
                />
                {formErrors.tax_id && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.tax_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  maxLength={255}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Contact Person"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email<span className='text-red-400'> *</span>
                </label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={255}
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.email ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                  placeholder="e.g. contact@company.com"
                />
                {formErrors.email && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={20}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Phone number"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Website
                </label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  maxLength={255}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. www.company.com"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Street address, City, ZIP"
                />
              </div>

              {/* Factories toggleable badges */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-300">
                    Factories<span className="text-red-400"> *</span>
                    {formData.factories?.length > 0 && (
                      <span className="ml-2 text-xs font-normal text-blue-400">
                        ({formData.factories.length} selected)
                      </span>
                    )}
                  </label>
                  {factories?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const allIds = factories.map((f) => f.id || f.ID).filter(Boolean);
                          setFormData((prev) => ({ ...prev, factories: allIds }));
                          setFormErrors((prev) => ({ ...prev, factories: undefined }));
                        }}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
                      >
                        Select all
                      </button>
                      <span className="text-gray-600 text-xs">|</span>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, factories: [] }));
                          setFormErrors((prev) => ({ ...prev, factories: 'At least one factory must be selected' }));
                        }}
                        className="text-xs text-gray-400 hover:text-gray-300 transition-colors cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {loadingFactories ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Loading factories...</span>
                  </div>
                ) : !factories || factories.length === 0 ? (
                  <p className="text-xs text-gray-400 py-1">No factories available</p>
                ) : (
                  <div
                    className={`flex flex-wrap gap-2 p-2.5 rounded-lg border bg-gray-700/40 transition-colors ${formErrors.factories ? 'border-red-500 border-2' : 'border-gray-700'
                      }`}
                  >
                    {factories.map((factory) => {
                      const factoryId = factory.id || factory.ID;
                      const isSelected = formData.factories?.includes(factoryId);
                      const initials = getFactoryInitials(factory);
                      const displayName = factory.name || factory.asset_code || initials;

                      return (
                        <button
                          key={factoryId || initials}
                          type="button"
                          onClick={() => handleToggleFactory(factoryId)}
                          title={displayName}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wider border transition-all duration-150 cursor-pointer select-none ${isSelected
                            ? 'bg-blue-600 border-blue-400 text-white shadow-md shadow-blue-900/30 ring-1 ring-blue-400'
                            : 'bg-gray-700/60 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-500'
                            }`}
                        >
                          {initials}
                        </button>
                      );
                    })}
                  </div>
                )}
                {formErrors.factories && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.factories}</p>
                )}
              </div>

              {isEdit && (
                <div className="col-span-2 flex items-center gap-2 py-2">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-gray-300 cursor-pointer">
                    Active / Active Partner Company
                  </label>
                </div>
              )}

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Additional notes about this company..."
                />
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-800 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors border border-gray-600 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

